export default function Eligibility() {
  const eligibleGroups = [
    {
      icon: 'ri-shield-fill',
      color: 'from-green-800 to-green-900',
      border: 'border-green-700/40',
      badge: 'bg-green-900/40 text-green-400',
      title: 'Active Duty',
      description: 'All active duty service members across all branches of the U.S. Armed Forces',
      branches: ['Army', 'Navy', 'Air Force', 'Marines', 'Coast Guard', 'Space Force']
    },
    {
      icon: 'ri-medal-fill',
      color: 'from-yellow-700 to-yellow-900',
      border: 'border-yellow-700/40',
      badge: 'bg-yellow-900/40 text-yellow-400',
      title: 'Veterans',
      description: 'Honorably discharged veterans — all branches, all eras. No DD-214 required.',
      branches: ['All branches', 'All eras', 'Honorable discharge']
    },
    {
      icon: 'ri-team-fill',
      color: 'from-slate-600 to-slate-800',
      border: 'border-slate-600/40',
      badge: 'bg-slate-700/60 text-slate-300',
      title: 'Reservists & Guard',
      description: 'Members of the Reserve and National Guard components, active or inactive',
      branches: ['Army Reserve', 'Navy Reserve', 'Air Force Reserve', 'National Guard']
    },
    {
      icon: 'ri-heart-fill',
      color: 'from-red-700 to-red-900',
      border: 'border-red-700/40',
      badge: 'bg-red-900/40 text-red-400',
      title: 'Military Spouses',
      description: 'Spouses of active duty, veterans, and reservists — and Gold Star families',
      branches: ['Active duty spouses', 'Veteran spouses', 'Gold Star families']
    }
  ];

  const firstResponderGroups = [
    {
      icon: 'ri-police-car-fill',
      color: 'from-blue-800 to-blue-900',
      border: 'border-blue-700/40',
      badge: 'bg-blue-900/40 text-blue-400',
      title: 'Law Enforcement',
      description: 'Active and retired police officers, sheriffs, deputies, and federal law enforcement agents',
      branches: ['Police Officers', 'Sheriffs & Deputies', 'State Troopers', 'Federal Agents', 'Retired LEO']
    },
    {
      icon: 'ri-fire-fill',
      color: 'from-orange-700 to-red-800',
      border: 'border-orange-600/40',
      badge: 'bg-orange-900/40 text-orange-400',
      title: 'Fire & Rescue',
      description: 'Career and volunteer firefighters, wildland firefighters, and fire investigators',
      branches: ['Career Firefighters', 'Volunteer Firefighters', 'Wildland Fire', 'Fire Investigators']
    },
    {
      icon: 'ri-first-aid-kit-fill',
      color: 'from-teal-700 to-teal-900',
      border: 'border-teal-600/40',
      badge: 'bg-teal-900/40 text-teal-400',
      title: 'EMT & Paramedics',
      description: 'Emergency medical technicians, paramedics, and flight medics serving their communities',
      branches: ['EMT-Basic', 'EMT-Advanced', 'Paramedics', 'Flight Medics', 'ER Nurses']
    },
    {
      icon: 'ri-phone-fill',
      color: 'from-purple-700 to-purple-900',
      border: 'border-purple-600/40',
      badge: 'bg-purple-900/40 text-purple-400',
      title: '911 Dispatchers',
      description: 'Emergency communications officers and 911 dispatchers — the first of the first responders',
      branches: ['911 Dispatchers', 'Emergency Comm.', 'Crisis Negotiators']
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Who Qualifies for the Dog Tag Discount?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            If you've served or are currently serving — in uniform or on the front lines — you qualify. It's that simple.
          </p>
        </div>

        {/* Military */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-slate-200"></div>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 px-3">Military & Veterans</span>
            <div className="h-px flex-1 bg-slate-200"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {eligibleGroups.map((group, index) => (
              <div key={index} className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border ${group.border}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${group.color} rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <i className={`${group.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{group.title}</h3>
                    <p className="text-slate-600 mb-4 text-sm leading-relaxed">{group.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {group.branches.map((branch, idx) => (
                        <span key={idx} className={`text-xs font-semibold ${group.badge} px-3 py-1 rounded-full border border-current/20`}>
                          {branch}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* First Responders */}
        <div className="mt-10 mb-12">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-slate-200"></div>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 px-3 flex items-center gap-1.5">
              <i className="ri-shield-star-fill text-red-500"></i>
              First Responders
            </span>
            <div className="h-px flex-1 bg-slate-200"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {firstResponderGroups.map((group, index) => (
              <div key={index} className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border ${group.border}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${group.color} rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <i className={`${group.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{group.title}</h3>
                    <p className="text-slate-600 mb-4 text-sm leading-relaxed">{group.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {group.branches.map((branch, idx) => (
                        <span key={idx} className={`text-xs font-semibold ${group.badge} px-3 py-1 rounded-full border border-current/20`}>
                          {branch}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* No Paperwork Callout */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 rounded-xl border-l-4 border-yellow-400">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-fill text-2xl text-yellow-400"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-3">No DD-214. No Badge Number. No Problem.</h3>
              <p className="text-lg text-slate-300 leading-relaxed">
                Rockhound Auto takes your word for it. Show us your ID, tell us you served or respond, or just ask
                — we don't care about paperwork. We're here to thank you for your service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
